// journal / dto — the Journal's wire contract (`readJournal`'s output).
// The zod schema IS the contract: the server answers with it, a client
// parses it at its boundary (`@bilda/app/journal`). Browser-safe: zod only.
//
// One period (day, month, year) per entry, newest
// first. Every entry has the same shape at every scale:
//
//   own        the Author's own markdown ("Du"), or null
//   assistant  the agent's prose summary ("Assistenten"), or null:
//                written    by the agent (AI-made; `basis` says from what)
//                corrected  by the Author (`locked`: the agent won't touch it)
//   links      the in-prose link targets resolved for chips (title + kind);
//              a link not listed here renders as plain text
//   detail     what lies underneath: a day's activity rows ("Vad hände"),
//              or a rollup's child periods with their texts
//
// Facts, never display text: dates are ISO ("2026-10-01"), instants are
// ISO timestamps, headings and "based on" lines are worded by the client.
export { journalViewDto };
import { z } from "zod";
const zScale = z.enum(["day", "month", "year"]);
const zIsoDate = z
    .string()
    .meta({ description: "A calendar date, YYYY-MM-DD." });
const zInstant = z.string().meta({ description: "An ISO 8601 instant." });
const zResourceKind = z.enum(["file", "source", "material", "export"]);
const zLinkTarget = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("resource_target"),
        href: z.string(),
        id: z.string(),
        resourceKind: zResourceKind,
        title: z.string(),
    }),
    z.object({
        type: z.literal("conversation_target"),
        href: z.string(),
        id: z.string(),
        title: z.string(),
    }),
]);
const zBasis = z.discriminatedUnion("type", [
    // A day: summarized from this many activity moments.
    z.object({ type: z.literal("activity_basis"), moments: z.number() }),
    // A rollup: summarized from these child periods' summaries.
    z.object({
        type: z.literal("rollup_basis"),
        children: z.array(z.string()),
    }),
]);
const zAssistant = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("written"),
        markdown: z.string(),
        writtenAt: zInstant,
        basis: zBasis,
        // The agent read the Author's own text too (only with consent).
        readOwnText: z.boolean(),
    }),
    z.object({
        type: z.literal("corrected"),
        markdown: z.string(),
        // The assistant's text the correction replaced (null: erased).
        original: z.string().nullable(),
        correctedAt: zInstant,
        locked: z.boolean(),
        basis: zBasis,
    }),
]);
const zOwn = z.object({ markdown: z.string(), writtenAt: zInstant });
const zActivityRow = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("resource_activity"),
        id: z.string(),
        resourceKind: zResourceKind,
        title: z.string(),
        // The latest moment that day.
        at: zInstant,
        // What happened to it that day, oldest first (history moment
        // kinds: ingested_source, authored_material, edited_material,
        // packaged_material, deleted_resource, restored_resource,
        // version_restored).
        moments: z.array(z.string()),
    }),
    z.object({
        type: z.literal("conversation_activity"),
        id: z.string(),
        title: z.string(),
        at: zInstant,
    }),
]);
const zChild = z.object({
    id: z.string(),
    scale: zScale,
    start: zIsoDate,
    end: zIsoDate,
    assistantMarkdown: z.string().nullable(),
    ownMarkdown: z.string().nullable(),
});
const zPeriod = z.object({
    id: z.string().meta({
        description: 'The period id: "2026-10-01", "2026-10" or "2026".',
    }),
    scale: zScale,
    start: zIsoDate,
    end: zIsoDate,
    // The period is over (local time). An open rollup gets no summary yet.
    closed: z.boolean(),
    own: zOwn.nullable(),
    assistant: zAssistant.nullable(),
    links: z.array(zLinkTarget),
    detail: z.discriminatedUnion("type", [
        z.object({
            type: z.literal("activity"),
            rows: z.array(zActivityRow),
        }),
        z.object({ type: z.literal("children"), children: z.array(zChild) }),
    ]),
});
const journalViewDto = z.object({
    scale: zScale,
    from: zIsoDate,
    to: zIsoDate,
    // May the assistant read the Author's own texts? (default disallowed)
    ownTextReading: z.enum(["allowed", "disallowed"]),
    // Newest first. Only periods with something in them: activity, a text,
    // or (for a rollup) a child with either.
    periods: z.array(zPeriod),
    // The `from` that reads the next older page ("Show older"); null when
    // nothing lies before this read.
    olderFrom: zIsoDate.nullable(),
});
//# sourceMappingURL=dto.js.map